// The screen before the match: the room, your deck, then Ready.
//
// It resolves once there is a game to play, and there are two kinds. A quick
// game builds a deck from the seed and plays the AI; a table is two people and
// a match running on the server. Everything above this -- the board, the HUD --
// does not care which it got.

import type { DeckList } from '../rules/engine.ts';
import type { CardIndex } from '../rules/cards.ts';
import type { BuildContext } from '../rules/deck.ts';
import { buildDeck, legalLegends, validateDeck } from '../rules/deck.ts';
import { exportDeck, importDeck, nameIndex } from './deckcode.ts';
import {
  formatRecord, type Profiles, type ReplayMeta, type Stats, type StoredDeck,
} from './profile.ts';
import type { Replay } from './log.ts';
import { ReplayBrowser } from './replaybrowser.ts';
import type { Lobby, LobbyMember, LobbyTable, LobbyView } from './lobby.ts';
import type { Difficulty } from '../ai/opponent.ts';
import type { ScriptIndex } from '../rules/effects.ts';
import { domainsHtml } from './symbols.ts';
import { load as loadSettings, save as saveSettings } from './settings.ts';
import type { MatchFrame } from './remote.ts';

const el = <T extends HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing #${id}`);
  return node as T;
};

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

export type SetupChoice =
  /** Play the AI with this deck; null means one built from the seed. */
  | {
    mode: 'ai'; deck: DeckList | null; deckId: string | null; difficulty: Difficulty;
    /** The legend the AI builds around, or null to let the seed decide. */
    aiLegend: string | null;
  }
  /** A match on the server, already begun. */
  | { mode: 'pvp'; table: string; frame: MatchFrame; deckId: string | null }
  /** Watch a match that already happened, rather than playing a new one. */
  | { mode: 'watch'; replay: Replay; meta: ReplayMeta };

export interface SetupOptions {
  index: CardIndex;
  ctx: BuildContext;
  /** Card ids with artwork on disk, so a tile never asks for a 404. */
  available: ReadonlySet<string>;
  /** Compiled abilities, so a preset deck prefers cards the engine models. */
  scripts: ScriptIndex;
  profiles: Profiles | null;
  stats: Stats | null;
  playerName: string;
  avatarUrl: string | null;
  /**
   * The room, when there already is one. Discord hands one over; a browser
   * has to be given a code first, which is what `joinRoom` is for.
   */
  lobby: Lobby | null;
  /**
   * Joins a room by code, connecting a lobby to it.
   *
   * Discord's instance id *is* the room, so inside the Activity this is never
   * set. Outside it there is nothing to derive a room from, so one player
   * mints a code and reads it to the other -- and from the moment both are in
   * it, everything below behaves exactly as it does in Discord, because a room
   * is keyed by a string either way. Resolves null if it could not connect.
   */
  joinRoom?: ((code: string) => Promise<Lobby | null>) | undefined;
  /** Mints an unused code. Present exactly when `joinRoom` is. */
  createRoom?: (() => Promise<string | null>) | undefined;
  /** This player's Discord id, for spotting themselves in the room. */
  userId: string | null;
  /** Why signing in failed, when it did. */
  signInError?: string | undefined;
}

export class Setup {
  private readonly opts: SetupOptions;
  private readonly root = el('setup');
  private readonly pickMine = el('setup-pick-mine');
  private readonly pickThem = el('setup-pick-them');
  private readonly picker = el('picker');
  private readonly pickerTitle = el('picker-title');
  private readonly pickerGrid = el('picker-grid');
  private readonly pickerImport = el('picker-import');
  private readonly pickerSearch = el<HTMLInputElement>('picker-search');
  private readonly tableList = el('setup-tables');
  private readonly memberList = el('setup-members');
  private readonly importBox = el<HTMLTextAreaElement>('setup-import');
  private readonly importName = el<HTMLInputElement>('setup-import-name');
  private readonly note = el('setup-note');
  private readonly roomPanel = el('setup-room');
  private readonly roomCurrent = el('setup-room-current');
  private readonly roomInput = el<HTMLInputElement>('setup-room-code');
  private readonly aiPick = el('setup-ai');
  private readonly ready = el<HTMLButtonElement>('setup-ready');
  private readonly detach: Array<() => void> = [];

  private decks: StoredDeck[] = [];
  private chosen: { deck: DeckList | null; deckId: string | null } = { deck: null, deckId: null };
  private names: ReturnType<typeof nameIndex> | null = null;
  private view: LobbyView = { members: [], tables: [] };
  private isReady = false;
  /** The room code, once there is one to show. Null in Discord: no code exists. */
  private code: string | null = null;
  /**
   * How hard the AI plays. Seeded from the stored preference, and overridden
   * for this match only by `?ai=` -- which is what the arena and the harnesses
   * pass, and they must not have their choice written back over the player's.
   */
  private difficulty: Difficulty;
  private readonly difficultyForced: boolean;
  /** The legend the AI should build around, or null for whatever the seed deals. */
  private aiLegend: string | null = null;
  /** Which side the modal is choosing for, or null while it is closed. */
  private picking: 'mine' | 'theirs' | null = null;
  private resolve: ((choice: SetupChoice) => void) | null = null;

  constructor(opts: SetupOptions) {
    this.opts = opts;
    const forced = typeof location === 'object'
      ? new URLSearchParams(location.search).get('ai') : null;
    this.difficultyForced = forced === 'easy' || forced === 'normal' || forced === 'hard';
    this.difficulty = this.difficultyForced
      ? forced as Difficulty
      : loadSettings().aiDifficulty;
  }

  /**
   * Hands a chosen recording back as the setup's answer.
   *
   * A replay resolves the same promise a match does, so everything downstream
   * -- the board, the HUD, the update hold -- follows one path rather than
   * growing a second entry point that has to be kept in step with the first.
   */
  private watch(replay: Replay, meta: ReplayMeta): void {
    const resolve = this.resolve;
    if (!resolve) return;
    this.resolve = null;
    this.close();
    resolve({ mode: 'watch', replay, meta });
  }

  async run(): Promise<SetupChoice> {
    this.names = nameIndex(this.opts.index);
    this.decks = this.opts.profiles ? await this.opts.profiles.decks() : [];

    el('setup-who').textContent = this.opts.playerName;
    const avatar = el<HTMLImageElement>('setup-avatar');
    if (this.opts.avatarUrl) { avatar.src = this.opts.avatarUrl; avatar.hidden = false; }
    const record = el('setup-record');
    record.textContent = this.opts.stats ? formatRecord(this.opts.stats.overall) : '';
    record.hidden = !this.opts.stats;

    if (this.opts.signInError) {
      this.say(`Discord sign-in failed: ${this.opts.signInError}. Playing as a guest.`, true);
    }

    const lobby = this.opts.lobby;
    if (lobby) {
      lobby.onMatch = (frame) => this.onMatch(frame);
      this.view = lobby.view;
    }

    this.renderChosen();
    this.renderDifficulty();
    this.renderRoom();

    // Saved matches are kept per person, so there is nothing to list for a
    // guest -- the button is not offered rather than offered and empty.
    const replaysButton = el<HTMLButtonElement>('setup-replays');
    replaysButton.hidden = !this.opts.profiles;
    if (this.opts.profiles) {
      const browser = new ReplayBrowser({
        profiles: this.opts.profiles,
        onWatch: (replay, meta) => this.watch(replay, meta),
      });
      replaysButton.addEventListener('click', () => void browser.open());
      this.detach.push(() => browser.close());
    }

    this.root.hidden = false;

    return new Promise<SetupChoice>((resolve) => {
      this.resolve = resolve;
      const on = (node: HTMLElement, event: string, fn: (e: Event) => void) => {
        node.addEventListener(event, fn);
        this.detach.push(() => node.removeEventListener(event, fn));
      };

      if (lobby) this.detach.push(() => { lobby.onMatch = null; });
      this.detach.push(() => {
        const late = this.opts.lobby;
        if (late) late.onMatch = null;
      });

      on(this.pickMine, 'click', () => this.openPicker('mine'));
      on(this.pickThem, 'click', () => this.openPicker('theirs'));
      on(el('picker-close'), 'click', () => this.closePicker());
      // Clicking the backdrop closes it; clicking the panel must not.
      on(this.picker, 'click', (e) => {
        if (e.target === this.picker) this.closePicker();
      });
      on(this.pickerSearch, 'input', () => this.renderPicker());
      // Escape closes the modal. On `document` rather than the panel, because
      // the focus may be anywhere inside it -- or nowhere, after a click on
      // the backdrop.
      const onEscape = (e: KeyboardEvent): void => {
        if (e.key === 'Escape' && this.picking) this.closePicker();
      };
      document.addEventListener('keydown', onEscape);
      this.detach.push(() => document.removeEventListener('keydown', onEscape));

      on(this.pickerGrid, 'click', (e) => {
        const tile = (e.target as HTMLElement | null)?.closest?.('[data-pick]');
        if (!(tile instanceof HTMLElement)) return;
        if ((e.target as HTMLElement).closest('[data-delete]')) {
          void this.remove(tile.dataset.id ?? '');
          return;
        }
        this.choose(tile.dataset.pick ?? '', tile.dataset.id ?? '');
      });

      on(this.aiPick, 'click', (e) => {
        const button = (e.target as HTMLElement | null)?.closest?.('[data-ai]');
        if (!(button instanceof HTMLElement) || this.difficultyForced) return;
        this.difficulty = button.dataset.ai as Difficulty;
        // Written through immediately rather than on Ready: the player may
        // never press Ready on this screen -- they may open a table instead --
        // and a preference that only sticks down one path is worse than none.
        saveSettings({ ...loadSettings(), aiDifficulty: this.difficulty });
        this.renderDifficulty();
      });

      on(el('setup-room-create'), 'click', () => { void this.makeRoom(); });
      on(el('setup-room-join'), 'click', () => { void this.enterRoom(); });
      on(this.roomInput, 'keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') void this.enterRoom();
      });
      // Codes are read aloud and typed in from another screen, so they are
      // shown and accepted in one case only. Doing it as the player types
      // means the box always shows the thing that will actually be sent.
      on(this.roomInput, 'input', () => {
        this.roomInput.value = this.roomInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      });

      on(this.tableList, 'click', (e) => {
        const button = (e.target as HTMLElement | null)?.closest?.('[data-act]');
        if (!(button instanceof HTMLElement)) return;
        void this.tableAction(button.dataset.act!, button.dataset.table!);
      });

      on(el('setup-open'), 'click', () => void this.openTable());
      on(el('setup-import-save'), 'click', () => void this.importPasted());
      on(this.ready, 'click', () => void this.onReady());
    });
  }

  /** Called by main when the lobby view changes. */
  onLobby(view: LobbyView): void {
    this.view = view;
    this.renderRoom();
  }

  private member(id: string | null): LobbyMember | undefined {
    return id ? this.view.members.find((m) => m.id === id) : undefined;
  }

  private myTable(): LobbyTable | undefined {
    const me = this.opts.userId;
    if (!me) return undefined;
    return this.view.tables.find(
      (t) => t.seats.includes(me) || t.spectators.includes(me));
  }

  // -- the room --------------------------------------------------------------

  /** Mints a code and joins the room it names. */
  /**
   * Who the AI plays. "Random" or a named legend, as a grid of the same tiles.
   *
   * The point is practice: a deck is built around a legend, so naming the
   * legend is naming the archetype you want to sit across from. Built once --
   * there are over a hundred of them and rebuilding the list on every render
   * would rewrite a hundred image tags to change one border.
   */
  private renderDifficulty(): void {
    // Array.from, not a for..of over the NodeList: the DOM lib this project
    // compiles against does not give NodeListOf an iterator.
    for (const button of Array.from(
      this.aiPick.querySelectorAll<HTMLButtonElement>('[data-ai]'))) {
      button.setAttribute('aria-checked', String(button.dataset.ai === this.difficulty));
      button.disabled = this.difficultyForced;
    }
    this.aiPick.title = this.difficultyForced
      ? 'Set by the ?ai= flag for this match'
      : '';
  }

  private async makeRoom(): Promise<void> {
    if (!this.opts.createRoom) return;
    this.say('Making a table…');
    const code = await this.opts.createRoom();
    if (!code) { this.say('Could not make a table. Try again.', true); return; }
    await this.attach(code);
  }

  private async enterRoom(): Promise<void> {
    const code = this.roomInput.value.trim().toUpperCase();
    if (code.length < 4) { this.say('That is not a code.', true); return; }
    await this.attach(code);
  }

  /**
   * Connects to a room and lets the rest of the screen carry on as though
   * Discord had provided it.
   *
   * The lobby is written back into the options because everything below reads
   * it from there -- one place that says whether there is a room, rather than
   * a second flag that could disagree with it.
   */
  private async attach(code: string): Promise<void> {
    if (!this.opts.joinRoom) return;
    const lobby = await this.opts.joinRoom(code);
    if (!lobby) { this.say(`Could not join ${code}.`, true); return; }
    this.opts.lobby = lobby;
    this.code = code;
    lobby.onMatch = (frame) => this.onMatch(frame);
    this.view = lobby.view;
    this.say(`In table ${code}.`);
    this.renderRoom();
  }

  private renderRoom(): void {
    // Three states, not two: in a room, able to make one, or unable to.
    const canMakeRoom = !!this.opts.joinRoom && !!this.opts.createRoom;
    this.roomPanel.hidden = !!this.opts.lobby || !canMakeRoom;
    this.roomCurrent.hidden = this.code === null;
    if (this.code !== null) {
      this.roomCurrent.innerHTML = `<div class="setup-hint">Your table's code --
        read it to whoever you are playing.</div>
        <div class="roomcode">${escapeHtml(this.code)}</div>`;
    }

    if (!this.opts.lobby) {
      // Inside the Activity a missing lobby means the Discord prompt was
      // dismissed rather than that there is no way to sign in -- and the two
      // want completely different things from the player.
      const cancelled = /cancel/i.test(this.opts.signInError ?? '');
      this.tableList.innerHTML = canMakeRoom
        ? ''
        : cancelled
          ? `<div class="setup-hint">Discord sign-in was cancelled, so this
             Activity does not know who you are and cannot seat you at a table.
             Close it and launch it again, then choose <b>Authorize</b>.</div>`
          : `<div class="setup-hint">Tables need a sign-in. Log in with Discord to
             play someone, here or in the Activity.</div>`;
      this.memberList.innerHTML = '';
      this.updateReadyButton();
      return;
    }

    const me = this.opts.userId;
    const mine = this.myTable();
    if (mine) this.isReady = !!me && mine.ready.includes(me);

    this.tableList.innerHTML = this.view.tables.length === 0
      ? `<div class="setup-hint">No tables yet. Open one and anyone in this
          activity can sit down.</div>`
      : this.view.tables.map((t) => this.tableRow(t)).join('');

    this.memberList.innerHTML = this.view.members.map((m) => `
      <span class="member">${m.avatar
        ? `<img src="https://cdn.discordapp.com/avatars/${escapeHtml(m.id)}/${escapeHtml(m.avatar)}.png?size=64" alt="">`
        : '<span class="pip"></span>'}${escapeHtml(m.name)}${m.away ? ' · away' : ''}</span>`).join('');

    this.updateReadyButton();
  }

  private tableRow(table: LobbyTable): string {
    const me = this.opts.userId;
    const atThis = !!me && (table.seats.includes(me) || table.spectators.includes(me));
    const seatHtml = table.seats.map((id) => {
      const who = this.member(id);
      if (!who) return '<div class="seat">Empty seat</div>';
      const ready = table.ready.includes(who.id);
      return `<div class="seat filled${ready ? ' ready' : ''}${who.away ? ' away' : ''}">
        ${who.avatar
          ? `<img src="https://cdn.discordapp.com/avatars/${escapeHtml(who.id)}/${escapeHtml(who.avatar)}.png?size=64" alt="">`
          : '<span class="pip"></span>'}
        ${escapeHtml(who.name)}
        ${who.away ? '<span class="seat-away">away</span>' : ready ? '<span class="seat-dot"></span>' : ''}
      </div>`;
    }).join('');

    const playing = table.status === 'playing';
    const seatsFull = table.seats.every((s) => s !== null);
    const acts: string[] = [];
    if (!atThis && !playing && !seatsFull) {
      acts.push(`<button data-act="sit" data-table="${table.id}" class="go">Sit down</button>`);
    }
    if (!atThis) {
      acts.push(`<button data-act="spectate" data-table="${table.id}">Watch</button>`);
    }
    if (atThis && !playing) {
      acts.push(`<button data-act="leave" data-table="${table.id}">Leave</button>`);
    }
    if (atThis && playing) {
      acts.push(`<button data-act="rejoin" data-table="${table.id}" class="go">Rejoin match</button>`);
    }

    return `
      <div class="table-row${atThis ? ' mine' : ''}">
        <div class="table-head">
          <span class="table-name">${escapeHtml(table.name)}</span>
          <span class="table-status">${playing ? 'in progress' : `${table.ready.length}/2 ready`}</span>
        </div>
        <div class="table-seats">${seatHtml}</div>
        <div class="table-acts">${acts.join('')}</div>
        ${table.spectators.length > 0
          ? `<div class="table-spec">${table.spectators.length} watching</div>` : ''}
      </div>`;
  }

  private async tableAction(act: string, table: string): Promise<void> {
    const lobby = this.opts.lobby;
    if (!lobby) return;
    if (act === 'sit') await lobby.sit(table);
    else if (act === 'spectate') await lobby.spectate(table);
    else if (act === 'leave') { this.isReady = false; await lobby.leave(); }
    else if (act === 'rejoin') this.say('Rejoining…');
  }

  private async openTable(): Promise<void> {
    const lobby = this.opts.lobby;
    if (!lobby) {
      this.say('Sign in with Discord to open a table.', true);
      return;
    }
    // No `window.prompt`. A Discord Activity is this page in a sandboxed
    // iframe, and that sandbox does not carry `allow-modals` -- so the prompt
    // never opens, returns null, and the function returned before it ever
    // reached the server. From the outside the button simply did nothing, and
    // the server log showed heartbeats and not one `/open`.
    //
    // The table is named after whoever opened it, which is what the prompt was
    // defaulting to anyway and what the lobby lists it by.
    this.say('Opening a table…');
    await lobby.open(`${this.opts.playerName}'s table`);
  }

  /** A match frame arriving means the table this player is at has begun. */
  private onMatch(frame: MatchFrame): void {
    if (!this.resolve) return;
    const resolve = this.resolve;
    this.resolve = null;
    this.close();
    resolve({ mode: 'pvp', table: frame.table, frame, deckId: this.chosen.deckId });
  }

  // -- readiness -------------------------------------------------------------

  private updateReadyButton(): void {
    const mine = this.myTable();
    if (!this.opts.lobby) {
      this.ready.textContent = 'Play the AI';
      this.ready.disabled = false;
      return;
    }
    if (!mine) {
      this.ready.textContent = 'Play the AI';
      this.ready.disabled = false;
      return;
    }
    const me = this.opts.userId;
    const seated = !!me && mine.seats.includes(me);
    if (!seated) {
      this.ready.textContent = 'Watching';
      this.ready.disabled = true;
      return;
    }
    this.ready.textContent = this.isReady ? 'Waiting for the other player…' : 'Ready';
    this.ready.disabled = this.isReady;
  }

  private async onReady(): Promise<void> {
    const mine = this.myTable();
    const me = this.opts.userId;
    // Not at a table: this button plays the AI, which is what it says.
    if (!this.opts.lobby || !mine || !me || !mine.seats.includes(me)) {
      const resolve = this.resolve;
      if (!resolve) return;
      this.resolve = null;
      this.close();
      resolve({
        mode: 'ai', deck: this.chosen.deck, deckId: this.chosen.deckId,
        difficulty: this.difficulty, aiLegend: this.aiLegend,
      });
      return;
    }
    this.isReady = true;
    this.updateReadyButton();
    this.say('Ready. Waiting for the other seat.');
    await this.opts.lobby.ready(mine.id, true, this.chosen.deck);
  }

  private close(): void {
    this.root.hidden = true;
    for (const off of this.detach) off();
    this.detach.length = 0;
  }

  // -- decks -----------------------------------------------------------------

  /**
   * One tile per deck: the champion's face, the name under it.
   *
   * A list of names told you nothing about a deck you had not opened -- decks
   * are *recognised* by their champion, which is the one thing a player names
   * them by out loud. The art is the card's own, cropped square to the band
   * the printed frame puts the character in.
   */
  private deckTile(opts: {
    /** What choosing it means: the seed's deck, a saved one, or a preset. */
    pick: 'quick' | 'random' | 'deck' | 'legend';
    id: string; card: string | null; name: string;
    /**
     * Already-safe markup, not text.
     *
     * A domain is shown as its rune, which is an element -- so every caller
     * escapes what it puts here, and none of it comes from a player except a
     * deck name, which `escapeHtml` handles at the point it is built.
     */
    sub: string;
    picked: boolean;
    deletable?: boolean;
  }): string {
    // `loading` and `decoding` both matter here and neither is enough alone.
    // A card render is 744x1039 and about 180 KB; the opponent grid holds one
    // for every legend in the game, and asking the browser for all of them at
    // once froze the tab outright -- not on the download, on decoding a
    // hundred-odd full-size images into 88px boxes. `content-visibility` on
    // the tile (see the CSS) is the third part: it keeps a tile that has been
    // scrolled past from being laid out or decoded at all.
    const art = opts.card && this.opts.available.has(opts.card)
      ? `<img class="tile-art" loading="lazy" decoding="async" alt=""`
        + ` src="/cards/${encodeURIComponent(opts.card)}.webp">`
      // A card with no artwork on disk still gets a tile, so a deck never
      // vanishes from the grid because its art failed to download.
      : '<span class="tile-art tile-blank"></span>';
    return `
      <button class="tile${opts.picked ? ' picked' : ''}" type="button"
              data-pick="${opts.pick}" data-id="${escapeHtml(opts.id)}"
              title="${escapeHtml(opts.name)}">
        <span class="tile-face">${art}${opts.deletable
          ? '<span class="tile-del" data-delete title="Delete this deck">&times;</span>' : ''}</span>
        <span class="tile-name">${escapeHtml(opts.name)}</span>
        <span class="tile-sub">${opts.sub}</span>
      </button>`;
  }

  /**
   * The one tile that says what is chosen, for either side.
   *
   * The lists live behind these. Inline, the opponent grid alone was 127
   * tiles, which made the lobby taller than the screen and pushed the button
   * you came here to press below the fold.
   */
  private renderChosen(): void {
    const mine = this.chosenCard();
    this.pickMine.innerHTML = this.chosenRow(mine.card, mine.name, mine.sub);

    const legend = this.aiLegend && this.opts.index.has(this.aiLegend)
      ? this.opts.index.get(this.aiLegend) : null;
    this.pickThem.innerHTML = this.chosenRow(
      legend?.id ?? null,
      legend ? legend.name : 'Random',
      legend ? domainsHtml(legend.domains) : 'Whatever the seed deals',
    );
  }

  /** A legend's domains by card id, for a deck that only stores the id. */
  private legendDomains(id: string): readonly string[] {
    return this.opts.index.has(id) ? this.opts.index.get(id).domains : [];
  }

  /** What the player's own side is set to right now. */
  private chosenCard(): { card: string | null; name: string; sub: string } {
    if (this.chosen.deckId) {
      const deck = this.decks.find((d) => d.id === this.chosen.deckId);
      if (deck) {
        const legend = this.opts.index.has(deck.list.legend)
          ? this.opts.index.get(deck.list.legend).name : 'unknown legend';
        return {
          card: deck.list.chosenChampion, name: deck.name,
          sub: `${domainsHtml(this.legendDomains(deck.list.legend))} ${escapeHtml(legend)}`,
        };
      }
    }
    if (this.chosen.deck) {
      const list = this.chosen.deck;
      const def = this.opts.index.has(list.legend) ? this.opts.index.get(list.legend) : null;
      const name = list.name || def?.name || 'Preset deck';
      // A preset takes its name from its legend, so printing the legend
      // underneath would say the same thing twice. The domains say what an
      // unnamed deck actually is.
      const sub = domainsHtml(def?.domains ?? [])
        + (def && def.name !== name ? ` ${escapeHtml(def.name)}` : '');
      return { card: list.chosenChampion, name, sub };
    }
    return { card: null, name: 'Quick game', sub: 'Built from this match’s seed' };
  }

  private chosenRow(card: string | null, name: string, sub: string): string {
    const art = card && this.opts.available.has(card)
      ? `<img class="tile-art" loading="lazy" decoding="async" alt=""`
        + ` src="/cards/${encodeURIComponent(card)}.webp">`
      : '<span class="tile-art tile-blank"></span>';
    return `<span class="tile-face">${art}</span>
      <span class="chosen-text">
        <span class="chosen-name">${escapeHtml(name)}</span>
        <span class="chosen-sub">${sub}</span>
      </span>
      <span class="chosen-go">CHANGE</span>`;
  }

  private openPicker(which: 'mine' | 'theirs'): void {
    this.picking = which;
    this.pickerTitle.textContent = which === 'mine' ? 'YOUR DECK' : 'THEIR DECK';
    // Only your own side can import a deck or keep one.
    this.pickerImport.hidden = which !== 'mine';
    this.pickerSearch.value = '';
    this.pickerGrid.dataset.query = 'never';
    this.renderPicker();
    this.picker.hidden = false;
    this.pickerSearch.focus();
  }

  private closePicker(): void {
    this.picking = null;
    this.picker.hidden = true;
    // Emptied on the way out. Left in place, a hundred-odd decoded card images
    // sit in memory for the rest of the match for a panel nobody is looking at.
    this.pickerGrid.innerHTML = '';
    this.pickerGrid.dataset.query = 'never';
    this.renderChosen();
  }

  /**
   * The grid inside the modal: presets, and for your own side your saved decks.
   *
   * Rebuilt only when the search changes. Swapping a selection border does not
   * need a hundred image tags rewritten, and rewriting them makes the browser
   * fetch and decode the lot again.
   */
  private renderPicker(): void {
    const which = this.picking;
    if (!which) return;
    const query = this.pickerSearch.value.trim().toLowerCase();

    if (this.pickerGrid.dataset.query !== query) {
      const tiles: string[] = [];
      const hit = (...fields: string[]): boolean =>
        !query || fields.some((f) => f.toLowerCase().includes(query));

      if (which === 'mine') {
        if (hit('quick game', 'seed')) {
          tiles.push(this.deckTile({
            pick: 'quick', id: '', card: null,
            name: 'Quick game', sub: 'Built from the seed',
            picked: !this.chosen.deck && !this.chosen.deckId,
          }));
        }
        const perDeck = this.opts.stats?.byDeck ?? {};
        const saved = this.decks.filter((d) => hit(d.name, d.list.legend));
        if (saved.length > 0) tiles.push('<div class="tiles-head">Your decks</div>');
        for (const deck of saved) {
          const legend = this.opts.index.has(deck.list.legend)
            ? this.opts.index.get(deck.list.legend).name : 'unknown legend';
          const record = perDeck[deck.id];
          tiles.push(this.deckTile({
            pick: 'deck', id: deck.id,
            // The Chosen Champion, not the legend: it is the card the deck is
            // built around and the one a player pictures when they name it.
            card: deck.list.chosenChampion,
            name: deck.name,
            sub: record
              ? `${domainsHtml(this.legendDomains(deck.list.legend))} ${escapeHtml(legend)}`
                + ` · ${escapeHtml(formatRecord(record))}`
              : `${domainsHtml(this.legendDomains(deck.list.legend))} ${escapeHtml(legend)}`,
            picked: this.chosen.deckId === deck.id,
            deletable: true,
          }));
        }
        if (!this.opts.profiles) {
          tiles.push('<div class="setup-hint tile-hint">Saved decks need a sign-in'
            + ' &mdash; log in with Discord to keep them. An imported deck lasts'
            + ' for this match.</div>');
        }
      } else if (hit('random', 'seed')) {
        tiles.push(this.deckTile({
          pick: 'random', id: '', card: null,
          name: 'Random', sub: 'Whatever the seed deals',
          picked: this.aiLegend === null,
        }));
      }

      // Presets, both sides: a deck is built around a legend, so naming the
      // legend names the archetype. Searched by champion tag as well as by
      // name, because the name a player knows is the champion's -- "ahri" has
      // to find "Nine-Tailed Fox", and through the tag it does.
      // One tile per legend, not per printing. `legalLegends` returns every
      // printing there is -- a base card, its alternate arts, its signed
      // version -- and for choosing an archetype those are one choice, listed
      // three times. The first printing wins because the list is in set and
      // number order, so that is the original rather than a variant.
      const seen = new Set<string>();
      const legends = legalLegends(this.opts.ctx)
        .filter((l) => hit(l.name, ...l.domains, ...l.tags))
        .filter((l) => (seen.has(l.name) ? false : (seen.add(l.name), true)));
      if (legends.length > 0) tiles.push('<div class="tiles-head">Preset decks</div>');
      for (const legend of legends) {
        tiles.push(this.deckTile({
          pick: 'legend', id: legend.id, card: legend.id,
          name: legend.name, sub: domainsHtml(legend.domains),
          picked: which === 'theirs'
            ? this.aiLegend === legend.id
            : !this.chosen.deckId && this.chosen.deck?.legend === legend.id,
        }));
      }

      if (tiles.length === 0) {
        tiles.push('<div class="setup-hint tile-hint">Nothing by that name.</div>');
      }
      this.pickerGrid.innerHTML = tiles.join('');
      this.pickerGrid.dataset.query = query;
    }
  }

  /** Builds a deck around a legend, for the presets. */
  private presetDeck(legendId: string): DeckList | null {
    if (!this.opts.index.has(legendId)) return null;
    // Its own stream, and not the match seed: a preset is a *chosen* deck, so
    // the match records it in the replay rather than claiming the seed
    // reproduces it.
    const rng = { seed: (Date.now() ^ 0x9e3779b9) >>> 0 };
    return buildDeck(this.opts.ctx, rng, this.opts.index.get(legendId),
      { scripts: this.opts.scripts, preferModelled: true });
  }

  /**
   * Takes a tile's answer, for whichever side the modal was opened for.
   *
   * The modal closes on a choice rather than needing a confirm: there is one
   * decision on screen and pressing a deck *is* making it.
   */
  private choose(pick: string, id: string): void {
    if (this.picking === 'theirs') {
      this.aiLegend = pick === 'legend' ? id : null;
      this.closePicker();
      return;
    }
    if (pick === 'quick') {
      this.chosen = { deck: null, deckId: null };
      this.say('');
      this.closePicker();
      return;
    }
    if (pick === 'legend') {
      const deck = this.presetDeck(id);
      if (!deck) { this.say('Could not build that deck.', true); return; }
      this.chosen = { deck, deckId: null };
      this.say(`${deck.name} built.`);
      this.closePicker();
      return;
    }
    const deck = this.decks.find((d) => d.id === id);
    if (!deck) return;
    // Validated when chosen rather than when saved: the card database can
    // change under a stored deck, and before the match is the honest time.
    const problems = validateDeck(this.opts.ctx, deck.list);
    if (problems.length > 0) {
      this.say(`${deck.name} cannot be played: ${problems.join('; ')}`, true);
      return;
    }
    this.chosen = { deck: deck.list, deckId: deck.id };
    this.say(`${deck.name} selected.`);
    this.closePicker();
  }

  private async remove(id: string): Promise<void> {
    if (!id || !this.opts.profiles) return;
    await this.opts.profiles.deleteDeck(id);
    this.decks = this.decks.filter((d) => d.id !== id);
    if (this.chosen.deckId === id) this.chosen = { deck: null, deckId: null };
    // The grid is keyed on the search text, so a deletion has to invalidate it
    // or the tile stays on screen until the next keystroke.
    this.pickerGrid.dataset.query = 'never';
    this.renderPicker();
    this.renderChosen();
  }

  private async importPasted(): Promise<void> {
    const text = this.importBox.value.trim();
    if (!text) { this.say('Paste a deck list first.', true); return; }

    const result = importDeck(text, this.opts.index, this.names ?? undefined);
    if (!result.deck) {
      this.say(`Could not read that deck: ${result.problems.join('; ')}`, true);
      return;
    }
    const problems = [...result.problems, ...validateDeck(this.opts.ctx, result.deck)];
    if (problems.length > 0) {
      this.say(`That deck is not playable: ${problems.join('; ')}`, true);
      return;
    }

    const name = this.importName.value.trim()
      || this.opts.index.get(result.deck.legend).name;

    if (!this.opts.profiles) {
      this.chosen = { deck: result.deck, deckId: null };
      this.say(`${name} loaded for this match. Sign in through Discord to save it.`);
      return;
    }
    const id = await this.opts.profiles.saveDeck(name, result.deck);
    if (!id) { this.say('Could not save that deck.', true); return; }

    this.decks = await this.opts.profiles.decks();
    this.chosen = { deck: result.deck, deckId: id };
    this.importBox.value = '';
    this.importName.value = '';
    this.say(`${name} saved.`);
    this.pickerGrid.dataset.query = 'never';
    this.renderPicker();
    this.renderChosen();
  }

  private say(text: string, bad = false): void {
    this.note.textContent = text;
    this.note.classList.toggle('bad', bad);
  }
}

export { exportDeck };
